import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, StrictMode, Suspense, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, useLocation } from 'react-router-dom';
import './index.css';

const GameApp = lazy(() => import('./App.js').then((module) => ({ default: module.App })));
const JournalApp = lazy(() => import('./features/journal/JournalApp.js'));

/** Select the application before mounting any game state, missions or tutorials. */
function AppSurface() {
  const { pathname } = useLocation();
  const journal = pathname === '/journal' || pathname.startsWith('/journal/');
  useEffect(() => {
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const theme = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    const originalIcon = icon?.href;
    document.title = journal ? 'Lombnapló' : 'RobotGyár';
    if (description)
      description.content = journal
        ? 'Egy saját fa a gondolataidnak és emlékeidnek.'
        : 'RobotGyár – matekgyakorlás, robotokkal.';
    if (theme) theme.content = journal ? '#102e50' : '#0d1014';
    if (journal && icon)
      icon.href = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><rect width="40" height="40" rx="10" fill="#102e50"/><path d="M17 34Q10 17 30 7Q32 30 17 28M17 30L27 12" fill="#a5bb78" stroke="#e6edc3" stroke-width="1.5"/></svg>')}`;
    return () => {
      if (icon && originalIcon) icon.href = originalIcon;
    };
  }, [journal]);
  return (
    <Suspense
      fallback={
        <div
          role="status"
          style={{
            minHeight: '100dvh',
            display: 'grid',
            placeItems: 'center',
            background: journal ? '#102e50' : '#0d1014',
            color: '#fff8df',
          }}
        >
          Betöltés…
        </div>
      }
    >
      {journal ? <JournalApp /> : <GameApp />}
    </Suspense>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // The server is the source of truth and every mutation returns fresh state,
      // so refetching on its own initiative would only fight the user.
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('Missing #root element');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppSurface />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
